import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  Key,
  CreditCard,
  Copy,
  Plus,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Upload,
  Wifi,
  RefreshCw,
  Building,
  FileText,
  Link2,
  ArrowLeftIcon,
  CircleAlert,
  Eye
} from 'lucide-react';
import Navbar from '@/components/modules/Navbar';
import QRForm from '@/components/modules/QRForm';
import UpgradePackage from '@/components/modules/UpgradePackage';
import Image from '@/components/modules/Image';
import { dialog } from '@/lib/dialog';

import useAxios from '@/hooks/use-axios';
import { useAuth } from '@/hooks/use-auth';
import { formatPrice, formatNumber } from '@/lib/numbers';

import { formatDateDisplay, getDaysUntilExpiry } from '@/lib/utils';

import { useInfiniteScroll } from '@/hooks/use-infinitescroll';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function UserDashboard() {
  const navigate = useNavigate();
  const api = useAxios();
  const { userData } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = () => {
    const tab = searchParams.get("tab");
    if(["listqr", "tokens", "packages", "payments"].includes(tab)) return tab;
    return "listqr";
  }

  const [activeTab, setActiveTab] = useState(initialTab());
  const [tokens, setTokens] = useState([]);
  const [listQR, setListQR] = useState([]);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(16000);
  const [bankSettings, setBankSettings] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState('monthly');

  // Dialogs
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showIpDialog, setShowIpDialog] = useState(false);
  const [showRenewalDialog, setShowRenewalDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [selectedQr, setSelectedQr] = useState(null);
  const [renewalToken, setRenewalToken] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);

  // Payment form
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [proofImage, setProofImage] = useState(null);
  const [proofFileName, setProofFileName] = useState('');

  // IP form
  const [newIp, setNewIp] = useState('');

  const [isEdit, setIsEdit] = useState(false);
  const [qrConfig, setQrConfig] = useState(null);
  const [tokenConfig, setTokenConfig] = useState(null);

  // QR form
  const [showQRForm, setShowQRForm] = useState(false);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [showUpgradePackage, setShowPackage] = useState(false);
  const [addNewPackage, setAddNewPackage] = useState(false);

  const [userPackages, setUserPackages] = useState([]);

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const [loadingTabData, setLoadingTabData] = useState(false);
  const [hasMore, setHasMore] = useState({
    tokens: true,
    listqr: true,
    packages: true,
    payments: true
  });

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const validTabs = ["listqr", "tokens", "packages", "payments"];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);


  const handleChangeTab = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setSize(15);
    navigate(`/dashboard?tab=${tab}`, { replace: true });
  };


  // Fungsi-fungsi fetch terpisah
  const fetchTokens = async () => {
    setLoadingTabData(true);
    const res = await api.get(`/user/tokens`);
    const data = res.data 
    setLoadingTabData(false);
    return data;
  };

  
  
  const fetchPackages = async () => {
    setLoadingTabData(true);
    const res = await api.get(`/packages/localized`);
    const data = res.data 
    setLoadingTabData(false);
    return data;
  };
  
  const fetchListQR = async () => {
    setLoadingTabData(true);
    const res = await api.get(`/qr-tracking`, {params: { page, size }});
    const data = res.data 
    setLoadingTabData(false);
    return data;
  };

  const fetchPayments = async (appendData =  false) => {
    setLoadingTabData(true);
    const res = await api.get(`/user/payments`, {params: { page, size }});
    const data = res.data.data
    setLoadingTabData(false);
    if(data.length < size) setHasMore({ ...hasMore, payments: false });
    if(appendData) setPayments([...payments, ...data]);
    return data;
  };

  const fetchUserPackages = async () => {
    setLoadingTabData(true);
    const res = await api.get(`/user/packages`, {params: { page, size }});
    const data = res.data.data
    setLoadingTabData(false);
    if(data.length < size) setHasMore({ ...hasMore, packages: false });
    return data;
  };

  // Fungsi utama untuk fetch all
  const fetchData = useCallback(async (loadingState = true) => {
    try {
      if (loadingState) setIsLoading(true);

      const [
        tokensRes,
        listQrRes,
        packagesRes,
        paymentsRes,
        userPackagesRes,
      ] = await Promise.all([
        fetchTokens(),
        fetchListQR(),
        fetchPackages(),
        fetchPayments(),
        fetchUserPackages(),
      ]);

      setTokens(tokensRes);
      setListQR(listQrRes);
      setCurrency(packagesRes.currency);
      setExchangeRate(packagesRes.exchange_rate);
      setPackages(packagesRes.packages);
      setPayments(paymentsRes);
      setUserPackages(userPackagesRes);

    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, []);

    useEffect(() => {
        if(activeTab === "payments" && hasMore.payments && !loadingTabData) fetchPayments(true);
    }, [page]);

    const loadMore = useCallback(() => {
        console.log("Loading more data...");
        setPage(prev => prev + 1);
    }, []);

    // Hook infinite scroll untuk Radix viewport
    useInfiniteScroll(containerRef, sentinelRef, loadMore, hasMore, loadingTabData);


  // Check if token is expiring soon (within 30 days)
  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const expires = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };


  const executePayPalPayment = async (paymentId, payerId) => {
    try {
      await api.post(`/paypal/execute?paypal_payment_id=${paymentId}&payer_id=${payerId}`);
      toast.success('Payment successful! Tokens have been created.');
      // Clear URL params
      navigate('/dashboard', { replace: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment execution failed');
    }
  };

  useEffect(() => {
    const paypalStatus = searchParams.get('paypal');
    const payerId = searchParams.get('PayerID');
    const paymentId = searchParams.get('paymentId');

    if (paypalStatus === 'success' && payerId && paymentId) {
      executePayPalPayment(paymentId, payerId);
    } else if (paypalStatus === 'cancel') {
      toast.error('Payment cancelled');
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success('Token copied to clipboard');
  };

  const handleProofUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setProofImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const handlePayment = async () => {
      if (!selectedPackage) return;
      try {
        setSubmitting(true);
        const data =await api.post(`/user/payments`, {
          package_id: selectedPackage.id,
        }).finally(() => setSubmitting(false));
        if(!data?.data.link) return; 
        openNewTab(data?.data.link, () => {
          setDetailInvoice(null);
          setShowPackage(false);
          setSelectedPackage(null);
          setShowPaymentDialog(false);
          navigate(`/dashboard?tab=payments`, { replace: true });
        })
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Perpanjangan gagal');
      }
    finally {
      fetchData(false);
      setSubmitting(false);
    }
  };

  const handlePayPalPayment = async () => {
    if (!selectedPackage) return;
    try {
      setSubmitting(true);
      const response = await api.post(`/paypal/create-order?package_id=${selectedPackage.id}`, {});
      // Redirect to PayPal
      if(!response?.data?.approval_url) return
      openNewTab(response.data.approval_url, () => {
        setDetailInvoice(null);
        setShowPackage(false);
        setSelectedPackage(null);
        setShowPaymentDialog(false);
        navigate(`/dashboard?tab=payments`, { replace: true });
      })
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat order PayPal');
    }
    finally {
      fetchData(false);
      setSubmitting(false);
    }
  };

const openNewTab = (linkUrl, onClose) => {
  const newTab = window.open(linkUrl, "_blank");

  if (newTab) {
    newTab.opener = null;
    const timer = setInterval(() => {
      if (newTab.closed) {
        clearInterval(timer);
        if (typeof onClose === "function") {
          onClose();
        }
      }
    }, 500);
  } else {
    console.error("Tab baru gagal dibuka (mungkin diblokir browser)");
  }
};




  const [isSubmitting, setSubmitting] = useState(false);
  const handleRenewalPayment = async () => {
    if (!renewalToken) return;
    try {
      if (paymentMethod === 'transfer') {
        setSubmitting(true);
        const data =await api.post(`/user/tokens/${renewalToken.id}/renew`, {
          package_id: renewalToken.package.id,
        }).finally(() => setSubmitting(false));
        if(!data?.data.link) return; 
        openNewTab(data?.data.link, () => {
          setDetailInvoice(null);
          setShowRenewalDialog(false);
          navigate(`/dashboard?tab=payments`, { replace: true });
        })
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Perpanjangan gagal');
    }
    finally {
      fetchData(false);
      setSubmitting(false);
    }
  };

  const handleRenewalPayPal = async () => {
    if (!renewalToken) return;
    try {
      setSubmitting(true);
      const response = await api.post(`/paypal/create-order?package_id=${renewalToken.package.id}`, {
        token_id: renewalToken.id
      });
      if(!response?.data?.approval_url) return
      openNewTab(response.data.approval_url, () => {
        setDetailInvoice(null);
        setShowRenewalDialog(false);
        navigate(`/dashboard?tab=payments`);
      })
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat order PayPal');
    } finally {
      fetchData(false);
      setSubmitting(false);
    }
  };

  const handleRefreshStatus = (paymentId) => {
    if(!paymentId) return
    let status = `${detailInvoice?.status}`;
    setDetailInvoice({...detailInvoice, status: '----'});
    api.get(`/user/payments/status/${paymentId}`).then(response => {
      if(response?.data) {
        status = response.data.data?.status;
        const payment = payments.find((p) => p.id === paymentId);
        if (payment) {
          payment.status = status;
          setPayments([...payments]);
        }
      }
    }).finally(() => {
      setTimeout(() => {
        if(detailInvoice !== null && detailInvoice.uid === paymentId) {
          setDetailInvoice({...detailInvoice, status: status});
        }
      }, 400)
    });
  }

  const handleUpdateIps = async () => {
    if (!selectedToken) return;
    try {
      const ips = [...(selectedToken.whitelisted_ips || [])];
      if (newIp && !ips.includes(newIp)) {
        ips.push(newIp);
      }

      await api.patch(`/user/tokens/${selectedToken.id}`, { whitelisted_ips: ips });

      toast.success('IP whitelist updated');
      setNewIp('');
      fetchData();
      setShowIpDialog(false);
    } catch (error) {
      toast.error('Failed to update IP whitelist');
    }
  };

  const removeIp = async (tokenId, ip) => {
    const token = tokens.find((t) => t.id === tokenId);
    if (!token) return;

    const ips = (token.whitelisted_ips || []).filter((i) => i !== ip);

    try {
      await api.patch(`/user/tokens/${tokenId}`, { whitelisted_ips: ips });
      toast.success('IP removed from whitelist');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove IP');
    }
  };

  const handleDeleteToken = () => {
    if(!selectedToken) return;
    dialog.confirm({
      title: 'Hapus QR',
      subtitle: 'Apakah Anda yakin untuk menghapus Token ini?',
      icon: <CircleAlert className="text-[#FFED93] text-[161px] max-w-md:w-1/2 max-w-md:h-1/2 w-full h-full max-w-[30%] max-h-[161px]" />,
      confirmText: 'Hapus',
      cancelText: 'Batal',
      onConfirm: async () => {
        try {
          await api.delete(`/user/qr/${selectedToken.id}`);
          toast.success('QR berhasil dihapus');
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const handleDeleteQR = (id) => {
    dialog.confirm({
      title: 'Hapus QR Code?',
      subtitle: 'Apakah Anda yakin untuk menghapus QR ini?',
      icon: <CircleAlert className="text-[#FFED93] text-[161px] max-w-md:w-1/2 max-w-md:h-1/2 w-full h-full max-w-[30%] max-h-[161px]" />,
      confirmText: 'Hapus',
      cancelText: 'Batal',
      onConfirm: async () => {
        try {
          await api.delete(`/qr-tracking/${id}`);
          fetchData();
          toast.success('QR berhasil dihapus');
        } catch (error) {
          console.error(error);
        }
      },
    });
  };

  const handleUpgradePackage = () => {
    setAddNewPackage(false);
    setShowPackage(true);
  };

  const fetchDetailQR = async (id) => {
    try {
      const response = await api.get(`/qr-tracking/${id}`);
      setQrConfig({ qr_config:response.data.qr_tracking_config, name: response.data.name, qr_path: response.data.qr_path });
      setShowQRForm(true);
    } catch (error) {
      console.error(error);
    }
  };
  const fetchDetailToken = async (id) => {
    try {
      const response = await api.get(`/user/tokens/${id}`);
      setTokenConfig({qr_config: response.data.qr_config, name: response.data.name});
      setShowTokenForm(true);
    } catch (error) {
      console.error(error);
    }
  };
  const handleEditQr = async (id) => {
    // console.log("selectedQr: ", selectedQr);
    // if(selectedQr === null) return;
    setIsEdit(true);
    setIsLoading(true);
    fetchDetailQR(id);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const handleEditToken = async (id) => {
    // console.log("selectedToken: ", selectedToken);
    // if(selectedToken === null) return;
    setIsEdit(true);
    setIsLoading(true);
    fetchDetailToken(id);
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const handlAddQR = () => {
    if (userData?.qr_tracking_quotas <= 0) {
      dialog.confirm({
        title: 'Batas Quota Token/QR Anda Tidak Cukup',
        subtitle: 'Upgrade paket untuk menambah kuota dan membuka akses fitur canggih lainnya.',
        icon: <CircleAlert className="text-[#FFED93] text-[161px] max-w-md:w-1/2 max-w-md:h-1/2 w-full h-full max-w-[30%] max-h-[161px]" />,
        confirmText: 'Lihat Paket',
        onConfirm: () => {
          setAddNewPackage(false);
          setShowPackage(true);
        },
      });
      return;
    }
    setIsEdit(false);
    setIsLoading(true);
    setQrConfig(null);
    setTimeout(() => {
      setIsLoading(false);
      setShowQRForm(true);
    }, 300);
  };

  const handleAddToken = () => {
    setIsEdit(false);
    setTokenConfig(null);
    if (tokens.length >= userData?.api_token_quotas) {
      const confirm = dialog.confirm({
        title: 'Batas Quota Token/QR Anda Tidak Cukup',
        subtitle: 'Upgrade paket untuk menambah kuota dan membuka akses fitur canggih lainnya.',
        icon: <CircleAlert className="text-[#FFED93] text-[161px] max-w-md:w-1/2 max-w-md:h-1/2 w-full h-full max-w-[30%] max-h-[161px]" />,
        confirmText: 'Lihat Paket',
        onConfirm: () => {
          setAddNewPackage(false);
          setShowPackage(true);
        },
      });
      return;
    }
  };

  useEffect(() => {
    console.log("re-render");
  }, [
    selectedQr,
    selectedToken,
    showUpgradePackage,
    showTokenForm,
    showQRForm,
    isLoading,
    isEdit
  ]);

  const OnGenerated = (data) => {
    setQrConfig(data);
  };

  const OnGeneratedToken = (data) => {
    setTokenConfig(data);
  };

  const submitQR = async () => {
    try {
      setSubmitting(true);
      if (isEdit) {
        await api.put(`/qr-tracking/${selectedQr.id}`, {...qrConfig.config, url_redirect: qrConfig.config.content});
      } else {
        await api.post(`/qr-tracking`, {...qrConfig.config, url_redirect: qrConfig.config.content});
      }
      fetchData();
      toast.success('QR Tracking berhasil ditambahkan');
      setShowQRForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan QR Tracking');
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpdateToken = async () => {
    try {
      setSubmitting(true);
      await api.patch(`/user/tokens/${selectedToken.id}`, {
        ...tokenConfig,
      });
      fetchData();
      toast.success('QR Tracking berhasil ditambahkan');
      setShowTokenForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan QR Tracking');
    } finally {
      setSubmitting(false);
    }
  };


  const OnPurchasePackage = (data) => {
    setSelectedPackage(data);
    setShowPaymentDialog(true);
    setProofImage(null);
    setProofFileName('');
    setPaymentMethod('transfer');
    // setShowPackage(true)
    setShowPaymentDialog(true);
  }


  
  function cleanUrlString(url) {
    if (!url) return 'qr_preview';
    try {
      const parsed = new URL(url);
      const host = parsed.hostname;
      return host.replace(/\./g, '_');
    } catch (err) {
      console.error('Invalid URL:', url);
      return '';
    }
  }

  const handleDownloadQR = (qr) => {
    const url = qr.content;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${cleanUrlString(qr.url_redirect)}.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (showTokenForm) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <section className="container px-4 lg:px-0 max-w-[1140px] flex flex-col gap-8 mx-auto py-8 ">
          <div className="flex flex-col w-full gap-4">
            <div className="flex flex-col gap-8 ">
              <Button variant="dark" className="w-fit" onClick={() => setShowTokenForm(false)}>
                {' '}
                <ArrowLeftIcon className="mr-2" /> Kembali
              </Button>
              <div className="flex justify-between">
                <h1 className="leading-[2.375rem] text-3xl font-semibold">
                  {isEdit ? 'Edit' : 'Tambah'} Token
                </h1>
              </div>
            </div>
          </div>
        </section>
        <QRForm OnGenerated={(e)=> OnGeneratedToken(e)} config={tokenConfig} hideInputContent={true} hideInputName={false} />
        <div className="flex gap-8 container mx-auto lg:px-0 max-w-[1140px] justify-end py-8 px-4">
          <Button variant="flat" className="min-w-44" onClick={() => setShowTokenForm(false)}>
            Cancel
          </Button>
          <Button
            className="min-w-44"
            onClick={() => submitUpdateToken()}
            disabled={!tokenConfig?.config?.content | isSubmitting}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }

  if (showQRForm) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <section className="container px-4 lg:px-0 max-w-[1140px] flex flex-col gap-8 mx-auto py-8">
          <div className="flex flex-col w-full gap-4">
            <div className="flex flex-col gap-8 ">
              <Button variant="dark" className="w-fit" onClick={() => setShowQRForm(false)}>
                {' '}
                <ArrowLeftIcon className="mr-2" /> Kembali
              </Button>
              <div className="flex justify-between">
                <h1 className="leading-[2.375rem] text-3xl font-semibold">
                  {isEdit ? 'Edit' : 'Tambah'} QR
                </h1>
              </div>
            </div>
          </div>
        </section>
        <QRForm OnGenerated={(e) => OnGenerated(e)} config={qrConfig} />
        <div className="flex gap-8 container lg:px-0 max-w-[1140px] mx-auto  justify-end py-8 px-4">
          <Button variant="flat" className="min-w-44" onClick={() => setShowQRForm(false)}>
            Cancel
          </Button>
          <Button className="min-w-44" onClick={() => submitQR()} disabled={!qrConfig?.config?.content | isSubmitting}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  if (showUpgradePackage) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <Navbar />
        <section className="container px-4 flex flex-col gap-8 mx-auto py-8">
          <div className="flex flex-col w-full gap-4">
            <div className="flex flex-col gap-8 ">
              <Button variant="dark" className="w-fit" onClick={() => setShowPackage(false)}>
                {' '}
                <ArrowLeftIcon className="mr-2" /> Kembali
              </Button>
              <div className="flex justify-between">
                <h1 className="leading-[2.375rem] text-3xl font-semibold">Upgrade Package</h1>
              </div>
            </div>
          </div>
        </section>
        <UpgradePackage OnPurchase={OnPurchasePackage} packages={packages} currency={currency} />

        
      {/* Payment Dialog - New Design */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Pembelian Paket {selectedPackage?.name}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="hidden"></DialogDescription>
          <div className="grid md:grid-cols-2 gap-6 mt-4">
            {/* Left Side - Invoice Details */}
            <div className="space-y-4">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-white">Invoice</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="">Paket</span>
                    <span className="text-white font-medium">{selectedPackage?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">Durasi</span>
                    <span className="text-white">{selectedPackage?.duration_months} Bulan {selectedPackage?.duration_months === 12 && '(1 Tahun)'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">API Token</span>
                    <span className="text-white">{selectedPackage?.websites_count} Token</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">API Hits/bulan</span>
                    <span className="text-white">
                      {selectedPackage?.api_hits_limit === 0 || !selectedPackage?.api_hits_limit
                        ? 'Unlimited'
                        : selectedPackage?.api_hits_limit?.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t border-zinc-800 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="">Total Pembayaran</span>
                      <span className="text-2xl font-bold text-primary-500">
                        {formatPrice(
                          (currency === 'IDR' ? selectedPackage?.price_idr : selectedPackage?.price) * (selectedPackage?.duration_months || 1),
                          currency,
                        )}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 text-right mt-1">/{selectedPackage?.duration_months === 12 ? 'tahun' : 'bulan'}</p>
                  </div>
                </div>
              </div>

              {/* Bank Account Info - Show when transfer selected */}
              {/* {paymentMethod === 'transfer' && bankSettings && bankSettings.bank_name && (
                <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-5 h-5 text-blue-400" />
                    <h4 className="font-semibold text-blue-400">Informasi Transfer</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="">Bank</span>
                      <span className="text-white font-medium">{bankSettings.bank_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="">No. Rekening</span>
                      <span className="text-white font-mono">{bankSettings.account_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="">Atas Nama</span>
                      <span className="text-white">{bankSettings.account_holder}</span>
                    </div>
                    {bankSettings.notes && (
                      <p className="text-xs text-blue-300 mt-2 pt-2 border-t border-blue-500/30">
                        {bankSettings.notes}
                      </p>
                    )}
                  </div>
                </div>
              )} */}
            </div>

            {/* Right Side - Payment Method & Upload */}
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300 text-sm">Metode Pembayaran</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    onClick={() => setPaymentMethod('transfer')}
                    className={`p-3 rounded-md border text-sm transition-all ${
                      paymentMethod === 'transfer'
                        ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                        : 'bg-zinc-950 border-zinc-800  hover:border-zinc-700'
                    }`}
                  >
                    <Building className="w-5 h-5 mx-auto mb-1" />
                    Local Payment
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypal')}
                    className={`p-3 rounded-md border text-sm transition-all ${
                      paymentMethod === 'paypal'
                        ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                        : 'bg-zinc-950 border-zinc-800  hover:border-zinc-700'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mx-auto mb-1" />
                    PayPal
                  </button>
                </div>
              </div>

              {paymentMethod === 'transfer' ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 text-center">
                    <p className="text-sm  mb-4">Bayar dengan aman menggunakan metode pembayaran lokal</p>
                    <Button
                      onClick={() => handlePayment()}
                      disabled={isSubmitting}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                    >
                      Bayar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 text-center">
                    <p className="text-sm  mb-4">
                      Bayar dengan aman menggunakan PayPal atau Kartu Kredit
                    </p>
                    <Button
                      disabled={isSubmitting}
                      onClick={() => handlePayPalPayment()}
                      className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.205 0 3.97.593 5.091 1.714.556.556.95 1.2 1.17 1.906.226.73.283 1.563.17 2.475-.12 1.028-.425 1.952-.9 2.748-.478.8-1.116 1.482-1.896 2.026-.786.548-1.727.96-2.797 1.224a14.36 14.36 0 0 1-3.317.376H7.943l-.867 5.777z" />
                      </svg>
                      Bayar dengan PayPal
                    </Button>
                  </div>
                  <p className="text-xs text-zinc-500 text-center">
                    Anda akan diarahkan ke PayPal untuk menyelesaikan pembayaran
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </div>
    );
  }

  return (
    <div className="h-screen bg-[#09090B] overflow-y-auto w-full"  ref={containerRef}>
      {/* Header */}

      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Dashboard</h1>

        <Tabs value={activeTab} className="w-full" onValueChange={handleChangeTab}>
          <TabsList className="bg-dark-foreground mb-[1.125rem] w-full flex-nowrap overflow-x-auto hide-scrollbar">
            <TabsTrigger value="listqr">
              <i
                className={`${activeTab === 'listqr' ? 'text-primary-500' : 'text-muted'} garuda-qrcode mr-2`}
              />
              My QR
            </TabsTrigger>
            <TabsTrigger value="tokens">
              <i
                className={`${activeTab === 'tokens' ? 'text-primary-500' : 'text-muted'} garuda-key mr-2`}
              />
              API
            </TabsTrigger>
            <TabsTrigger value="packages">
              <i
                className={`${activeTab === 'packages' ? 'text-primary-500' : 'text-muted'} garuda-wallet mr-2`}
              />
              My Packages
            </TabsTrigger>
            <TabsTrigger value="payments">
              <i
                className={`${activeTab === 'payments' ? 'text-primary-500' : 'text-muted'} garuda-wallet mr-2`}
              />
              Payment History
            </TabsTrigger>
          </TabsList>

          {/* QR Tab */}
          <TabsContent value="listqr">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col lg:flex-row justify-between ">
                <h2 className="text-lg font-bold text-white mb-4">
                  My QR{' '}
                  <span className="text-muted-foreground">
                    (Quota QR : {userData?.qr_tracking_quotas || 0})
                  </span>
                </h2>
                <Button className="min-w-[166px]" onClick={() => handlAddQR()}>
                  Tambah QR
                </Button>
              </div>
              {listQR.length === 0 && (
                <div className="flex flex-col justify-center items-center">
                  <i className={`text-primary-500 garuda-qrcode !text-[56px] !w-[56px]`} />
                  <p className="mt-4 text-muted-foreground">Belum ada QR Dibuat</p>
                  <p className="text-muted-foreground">
                    Mulai buat QR Code dan pantau statistik scan Anda di sini.
                  </p>
                </div>
              )}
              <div className="space-y-6">
                {listQR.length > 0 &&
                  listQR.map((qr) => (
                    <div className="card !p-6 flex flex-col lg:flex-row gap-6" key={qr.id}>
                      <div className="max-lg:w-full max-lg:h-52 lg:size-[88px] rounded-lg">
                        <Image
                          popupImage={!!qr.content}
                          alt={qr.content}
                          content={qr.content}
                          className=" max-lg:w-full max-lg:h-52 lg:size-[88px] mx-auto rounded-lg"
                        />
                      </div>
                      <div className="flex flex-col w-auto max-md:gap-2">
                        <div className="flex">
                          <Clock className="size-6 mr-2" />
                          <h4 className="text-base">{formatDateDisplay(qr.created_at)}</h4>
                        </div>
                        <a href={qr.url_redirect} target="_blank" className="flex mt-auto">
                          <Link2 className="size-6 mr-2" />
                          <h4 className="text-base">{qr.url_redirect}</h4>
                        </a>
                      </div>
                      <div className="flex flex-col w-full lg:max-w-[384px] ml-auto max-md:gap-2">
                        <div className="flex">
                          <Clock className="size-6 mr-2" />
                          <h4 className="text-base">Scans : </h4>
                          <Button variant="dark" className="ml-auto">
                            <Link to={`/dashboard/statistic/${qr.id}`}>Statistic Tracking</Link>
                          </Button>
                        </div>
                        <div className="flex my-auto">
                          <h4 className="text-[2rem]">{qr.scan_count}</h4>
                        </div>
                      </div>
                      <div className="bg-[#eaecf033] w-[1px] h-[88px] hidden lg:block"></div>
                      <div className="flex flex-row lg:flex-col max-sm:gap-2 lg:min-w-[215px]">
                        <Button
                          className="mx-auto lg:min-w-[166px]"
                          disabled={!qr.content}
                          onClick={() => handleDownloadQR(qr)}
                        >
                          Download QR
                        </Button>
                        <div className="flex gap-2 md:gap-6 w-full mt-auto">
                          <Button variant="flat" onClick={() => {
                                setIsEdit(true);
                                setQrConfig(null);
                                setSelectedQr(qr);
                                handleEditQr(qr.id);
                              }
                            }>
                            <i className="garuda-palette" />
                            Edit
                          </Button>
                          <Button variant="flat" onClick={() => handleDeleteQR(qr.id)}>
                            <i className="garuda-delete" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </TabsContent>

          {/* Token Tab */}
          <TabsContent value="tokens">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col lg:flex-row justify-between ">
                <h2 className="text-lg font-bold text-white mb-4">
                  My Token <span className="text-muted-foreground">(Quota Token : { userData?.api_token_quotas || 0 })</span>
                </h2>
                <Button className="min-w-[166px]" onClick={() => handleAddToken()}>
                  Tambah Token
                </Button>
              </div>
              {tokens.length === 0 ? (
                <div className="rounded-lg p-12 bg-dark-foreground text-center">
                  <Key className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-lg font-semibold text-white mb-2">Belum Ada API Token</h3>
                  <p className=" mb-4">Beli paket untuk mendapatkan API token</p>
                  <Button
                    onClick={() =>  {
                      setAddNewPackage(true); 
                      setShowPackage(true);
                    }}
                    className="bg-primary-500 hover:bg-primary-600"
                  >
                    Lihat Paket
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tokens.map((token) => (
                    <div key={token.id} className="card !p-6 flex flex-col gap-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex flex-col lg:flex-row gap-6 w-full">
                          <div className="max-lg:w-full max-lg:h-52 lg:size-[88px] rounded-lg">
                            <Image
                              content={token?.content}
                              className="max-lg:w-full max-lg:h-52 lg:size-[88px] mx-auto rounded-lg"
                            />
                          </div>
                          <div className="flex flex-col w-auto max-md:gap-2">
                            <h4 className="text-base">{token.name}</h4>
                            <h4 className="text-base mt-auto flex">
                              Expired :{formatDateDisplay(token.expires_at)}
                              {token.expires_at && (
                                <Badge className="capitalize ml-4 bg-orange-400/30 text-orange-200">
                                  {getDaysUntilExpiry(token.expires_at)} Hari lagi
                                </Badge>
                              )}
                            </h4>
                          </div>
                          <div className="flex flex-col w-full lg:max-w-[384px] max-md:gap-2 ml-auto ">
                            <h4 className="text-base">
                              Api Request Counter : {formatNumber(token.api_hits_used)} / {token.api_hits_limit != 0 ? formatNumber(token.api_hits_limit) : 'Unlimited'}
                            </h4>
                            <h4 className="text-base mt-auto">
                              Status :{' '}
                              <Badge
                                className={
                                  token.status === 'active'
                                    ? 'status-active capitalize'
                                    : 'status-disabled capitalize'
                                }
                              >
                                {token.status}
                              </Badge>
                            </h4>
                          </div>
                          <div className="bg-[#eaecf033] w-[1px] h-[88px] hidden lg:block"></div>
                          <div className="flex flex-row lg:flex-col max-sm:gap-2 lg:min-w-[215px]">
                            <div className="flex gap-6 w-full my-auto">
                              <Button
                                variant="flat"
                                onClick={() => {
                                  setIsEdit(true);
                                  setTokenConfig(null);
                                  setSelectedToken(token);
                                  handleEditToken(token.id)
                                }}
                                className="lg:w-full"
                              >
                                <i className="garuda-palette" />
                                Edit
                              </Button>
                              {/* <Button variant="flat" onClick={async () => {
                                  await setSelectedToken(token)
                                  handleDeleteToken()
                                }}>
                                <i className="garuda-delete" />
                                Delete
                              </Button> */}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label className="text-sm text-muted">Token QR</Label>
                        <div className="w-full flex gap-6">
                          <div className="bg-black p-3 rounded-lg w-full">
                            <code className="rounded-md p-3 text-xs text-primary-400 font-mono break-all align-middle contents">
                              {token.token}
                            </code>
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToken(token.token)}
                              className=" hover:text-white"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Whitelisted IPs */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-sm text-muted">IP Whitelist</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedToken(token);
                              setShowIpDialog(true);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add IP
                          </Button>
                        </div>

                        {!token.whitelisted_ips || token.whitelisted_ips.length === 0 ? (
                          <p className="text-xs text-zinc-500 italic">
                            No IP whitelist. API accessible from any IP address.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {token.whitelisted_ips.map((ip) => (
                              <div
                                key={ip}
                                className="flex items-center gap-1 px-2 py-1 rounded bg-zinc-800 text-xs text-zinc-300"
                              >
                                <Wifi className="w-3 h-3 text-zinc-500" />
                                {ip}
                                <button
                                  onClick={() => removeIp(token.id, ip)}
                                  className="text-zinc-500 hover:text-red-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Active Packages Tab */}
          <TabsContent value="packages">
            {userPackages.length === 0 ? (
              <div className="rounded-lg p-12 bg-dark-foreground text-center">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <h3 className="text-lg font-semibold text-white mb-2">No Package Purchased</h3>
                <p className="">Your package history will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userPackages.map((aPackage) => (
                  <div key={aPackage.id} className="card !p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                      <div>
                        <h3 className="font-semibold text-white">
                          Package : {aPackage.package?.name || 'Unknown Package'}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1">
                          Created at:   {new Date(aPackage.created_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Expired at: {new Date(aPackage.expires_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-center gap-4">
                        <h3 className="text-sm text-white">
                          Token Name : {aPackage?.name}
                        </h3>
                        <h3 className="text-white text-sm flex items-center">
                          <span>
                            Status : 
                          </span>
                          <Badge
                              className={`ml-2 capitalize ${
                                aPackage.status === 'active'
                                  ? 'status-active'
                                  : aPackage.status === 'expired'
                                    ? 'status-disabled'
                                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                              }`}
                            >
                              {aPackage.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                              {aPackage.status === 'expired' && <XCircle className="w-3 h-3 mr-1" />}
                              {aPackage.status}
                            </Badge>
                          </h3>
                      </div>
                      <div className="flex flex-row lg:flex-col max-sm:gap-2">
                        {isExpiringSoon(aPackage.expires_at) || aPackage.status === 'expired' ? (
                          <Button
                            onClick={() => {
                              setRenewalToken(aPackage);
                              setProofImage(null);
                              setProofFileName('');
                              setPaymentMethod('transfer');
                              setShowRenewalDialog(true);
                            }}
                            className="mx-auto lg:min-w-[166px]"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Perpanjang
                          </Button>
                        ) : (
                          <Button
                            className="mx-auto lg:min-w-[166px]"
                            onClick={() => {
                                setRenewalToken(aPackage);
                                handleUpgradePackage();
                            } 
                            }>
                            Update Package
                          </Button>
                        )}
                      </div>
                    </div>
                  
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          
          {/* Payments Tab */}
          <TabsContent value="payments">

              {payments.length === 0 ? (
                <div className="rounded-lg p-12 bg-dark-foreground text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                  <h3 className="text-lg font-semibold text-white mb-2">No Payment History</h3>
                  <p className="">Your payment history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment) => (
                    <div key={payment.id} className="card !p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          {/* <h3 className="font-semibold text-white">
                            {payment.package?.name || 'Package'}
                          </h3> */}
                          <p className="text-sm mt-2">
                            ( {payment.package?.name || 'Package'} ) 
                            &nbsp;
                            { formatPrice(payment?.amount, payment?.currency) }  
                            &nbsp; via {payment.payment_method}
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {new Date(payment.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          
                        </div>
                        <div className='flex flex-col md:flex-row gap-2'>
                          <Badge
                            className={`capitalize ${
                              payment.status === 'approved'
                                ? 'status-active'
                                : ['expired', 'rejected', 'failed'].includes(payment.status)
                                  ? 'status-disabled'
                                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                            }`}
                          >
                            {payment.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {payment.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                            {payment.status.replace('_', ' ')}
                          </Badge>
                          { ['pending', 'waiting_payment'].includes(payment.status) && <Button
                            onClick={() => {
                              setDetailInvoice(payment);
                              setShowInvoiceDialog(true);
                            }}
                            className="mx-auto"
                            variant="outline"
                            size="sm"
                            title="Refresh Status"
                          >
                              <Eye className="w-4 h-4 mr-1" />
                              Invoice
                          </Button>}
                        </div>
                      </div>
                      {payment.admin_notes && (
                        <p className="text-sm text-red-400 mt-2">Note: {payment.admin_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </TabsContent>
        </Tabs>
        { loadingTabData && <div className="rounded-full w-12 h-12 bg-[#09090B] flex items-center justify-center absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="spinner !w-6 !h-6" />
          </div>
        }
      </main>
      
      <div ref={sentinelRef} style={{ height: 10, opacity: 0, background: 'blue' }} />


      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog} onClose={() => setDetailInvoice(null)} >
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary-500" />
              Detail Payment
            </DialogTitle>
          </DialogHeader>

          <div className="grid md:grid-cols-1 gap-6 mt-4">
            {/* Left Side - Token & Invoice Details */}
            <div className="space-y-4">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-white">Invoice</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="">Nama Paket</span>
                    <span className="text-white">{detailInvoice?.package?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">Durasi</span>
                    <span className="text-white">{detailInvoice?.package.duration_months} Bulan</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">API Hits/bulan</span>
                    <span className="text-white">
                      {detailInvoice?.package.api_hits_limit === 0 || !detailInvoice?.package.api_hits_limit
                        ? 'Unlimited'
                        : detailInvoice?.package.api_hits_limit?.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t border-zinc-800 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="">Total Pembayaran</span>
                      <span className="text-2xl font-bold text-primary-500">
                        {formatPrice((detailInvoice?.amount || 0), detailInvoice?.currency)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 text-right mt-1">/{detailInvoice?.package.duration_months === 12 ? 'tahun' : 'bulan'}</p>
                  </div>
                </div>
              </div>
              <div className="pt-3 mt-3 flex justify-between">
                <h3 className="font-semibold text-white">Status Pembayaran</h3>
                <div className="mt-2 flex items-center">
                  <Badge
                    className={`capitalize ${
                      detailInvoice?.status === 'approved'
                        ? 'status-active'
                        : ['expired', 'rejected', 'failed'].includes(detailInvoice?.status)
                          ? 'status-disabled'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`}
                  >
                    {detailInvoice?.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                    {detailInvoice?.status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
                    {detailInvoice?.status?.replace('_', ' ')}
                  </Badge>
                  <Button className="mx-auto" variant="flat" size="sm" title="Refresh Status" onClick={() => handleRefreshStatus(detailInvoice?.uid)} >
                    <RefreshCw className="w-4 h-4 mr-1" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-end pt-8">
                <Button variant="" onClick={() => openNewTab(detailInvoice?.invoice_url)}>Bayar Sekarang</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Token Renewal Dialog */}


      <Dialog open={showRenewalDialog} onOpenChange={setShowRenewalDialog}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary-500" />
              Perpanjang Token
            </DialogTitle>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-6 mt-4">
            {/* Left Side - Token & Invoice Details */}
            <div className="space-y-4">
              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
                <h4 className="font-semibold text-white mb-3">Informasi Token</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="">Nama Paket</span>
                    <span className="text-white">{renewalToken?.package?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">Kadaluarsa</span>
                    <span className="text-red-400">
                      {formatDateDisplay(renewalToken?.expires_at)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">Sisa Hari</span>
                    <span className="text-yellow-400 font-medium">
                      {renewalToken?.expires_at ? getDaysUntilExpiry(renewalToken.expires_at) : 0}{' '}
                      hari
                    </span>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-zinc-950 border border-zinc-800 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-white">Invoice Perpanjangan</h3>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="">Durasi</span>
                    <span className="text-white">+{renewalToken?.package.duration_months} Bulan</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="">API Hits/bulan</span>
                    <span className="text-white">
                      {renewalToken?.package.api_hits_limit === 0 || !renewalToken?.package.api_hits_limit
                        ? 'Unlimited'
                        : renewalToken?.package.api_hits_limit?.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t border-zinc-800 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="">Total Pembayaran</span>
                      <span className="text-2xl font-bold text-primary-500">
                        {formatPrice( (currency === 'IDR' ? renewalToken?.package.price_idr : renewalToken?.package.price) * (renewalToken?.package.duration_months || 1), currency)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 text-right mt-1">/{ renewalToken?.package.duration_months == 12 ? 'tahun' : 'bulan' }</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Payment Method & Upload */}
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300 text-sm">Metode Pembayaran</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    onClick={() => setPaymentMethod('transfer')}
                    className={`p-3 rounded-md border text-sm transition-all ${
                      paymentMethod === 'transfer'
                        ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                        : 'bg-zinc-950 border-zinc-800  hover:border-zinc-700'
                    }`}
                  >
                    <Building className="w-5 h-5 mx-auto mb-1" />
                    Local Payment
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypal')}
                    className={`p-3 rounded-md border text-sm transition-all ${
                      paymentMethod === 'paypal'
                        ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                        : 'bg-zinc-950 border-zinc-800  hover:border-zinc-700'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mx-auto mb-1" />
                    PayPal
                  </button>
                </div>
              </div>

              {paymentMethod === 'transfer' ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 text-center">
                    <p className="text-sm  mb-4">Bayar dengan aman menggunakan metode pembayaran lokal</p>
                    <Button
                      onClick={() => handleRenewalPayment()}
                      disabled={isSubmitting}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
                    >
                      Bayar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 text-center">
                    <p className="text-sm  mb-4">Bayar dengan aman menggunakan PayPal</p>
                    <Button
                      onClick={() => handleRenewalPayPal()}
                      className="w-full bg-[#0070ba] hover:bg-[#005ea6] text-white"
                      disabled={isSubmitting}
                    >
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.205 0 3.97.593 5.091 1.714.556.556.95 1.2 1.17 1.906.226.73.283 1.563.17 2.475-.12 1.028-.425 1.952-.9 2.748-.478.8-1.116 1.482-1.896 2.026-.786.548-1.727.96-2.797 1.224a14.36 14.36 0 0 1-3.317.376H7.943l-.867 5.777z" />
                      </svg>
                      Bayar dengan PayPal
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* IP Whitelist Dialog */}
      <Dialog open={showIpDialog} onOpenChange={setShowIpDialog}>
        <DialogContent className="bg-dark-foreground border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Manage IP Whitelist</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <p className="text-sm ">
              Only requests from these IP addresses will be allowed to use your API token. Leave
              empty to allow all IPs. Supports individual IPs and CIDR ranges (e.g.,
              192.168.1.0/24).
            </p>

            <div className="flex gap-2">
              <Input
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder="192.168.1.1 or 10.0.0.0/8"
                className="bg-zinc-950 border-zinc-800"
              />
              <Button
                onClick={() => handleUpdateIps()}
                className="bg-primary-500 hover:bg-primary-600"
              >
                Add
              </Button>
            </div>

            {selectedToken?.whitelisted_ips?.length > 0 && (
              <div>
                <Label className="text-sm ">Current IPs</Label>
                <div className="mt-2 space-y-2">
                  {selectedToken.whitelisted_ips.map((ip) => (
                    <div
                      key={ip}
                      className="flex items-center justify-between p-2 rounded bg-zinc-950 border border-zinc-800"
                    >
                      <span className="text-sm text-zinc-300 flex items-center gap-2">
                        <Wifi className="w-4 h-4 text-zinc-500" />
                        {ip}
                      </span>
                      <button
                        onClick={() => removeIp(selectedToken.id, ip)}
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
